import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { ReactNode } from 'react'

import rem from '~/utils/ui'

export type TAssistantOption = {
  label: string
  value: string
}

type TProps = {
  helperText?: ReactNode
  isLoading: boolean
  label?: string
  limitTags?: number
  name: string
  nonRemovableValues?: string[]
  options: TAssistantOption[]
  unavailableValues?: string[]
}

function AssistantMultiSelect({
  helperText = null,
  isLoading,
  label = '',
  limitTags = 1,
  name,
  nonRemovableValues = [],
  options,
  unavailableValues = [],
}: TProps) {
  const theme = useTheme()
  const [field, meta, helpers] = useField<string[]>(name)
  const { isSubmitting } = useFormikContext()
  const unavailableValueSet = new Set(unavailableValues)
  const nonRemovableValueSet = new Set(nonRemovableValues)
  const selectedAgentIds = field.value ?? []
  const selectedOptions = options.filter((option) =>
    selectedAgentIds.includes(option.value),
  )
  const hasError = meta.touched && typeof meta.error === 'string'
  const isDisabled = isLoading || isSubmitting
  const showFormHelperText = hasError || Boolean(helperText)
  const hasNonRemovableValues = nonRemovableValues.length > 0

  return (
    <FormControl error={hasError} fullWidth sx={{ gap: rem(8) }}>
      {!!label && (
        <FormLabel
          sx={{
            '&, &.Mui-focused': {
              color: theme.palette.mainFont,
              fontSize: rem(14),
              fontWeight: 500,
            },
          }}
        >
          {label}
        </FormLabel>
      )}
      <Autocomplete
        disableClearable={hasNonRemovableValues}
        disableCloseOnSelect
        disabled={isDisabled}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) =>
          unavailableValueSet.has(option.value) ||
          (nonRemovableValueSet.has(option.value) &&
            selectedAgentIds.includes(option.value))
        }
        isOptionEqualToValue={(option, value) => option.value === value.value}
        limitTags={limitTags}
        loading={isLoading}
        multiple
        onBlur={() => helpers.setTouched(true)}
        onChange={(_event, selected) => {
          const selectedIds = selected.map((option) => option.value)
          const nextValue = hasNonRemovableValues
            ? [...new Set([...nonRemovableValues, ...selectedIds])]
            : selectedIds

          helpers.setValue(nextValue)
        }}
        options={options}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, onDelete, ...chipProps } = getTagProps({ index })
            const isNonRemovable = nonRemovableValueSet.has(option.value)

            return (
              <Chip
                key={key}
                label={option.label}
                {...chipProps}
                onDelete={isNonRemovable ? undefined : onDelete}
                sx={{
                  height: rem(24),
                  maxWidth: rem(130),
                }}
              />
            )
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            error={hasError}
            placeholder={selectedOptions.length ? '' : 'Select assistants'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && (
                    <CircularProgress color="inherit" size={rem(16)} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: rem(210),
              width: '100%',
              '& .MuiInputBase-input': {
                paddingBlock: 0,
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: rem(8),
                minHeight: rem(36),
                paddingBlock: 0,
              },
            }}
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <li {...props} key={key}>
            <Typography noWrap variant="body2">
              {option.label}
            </Typography>
          </li>
        )}
        value={selectedOptions}
      />
      {showFormHelperText ? (
        <FormHelperText error={hasError}>
          {hasError ? meta.error : helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  )
}

export default AssistantMultiSelect
